import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from './store';
import { authKeys } from './queries';
import type { AuthenticatedUser } from './types';

export interface LoginInput {
  email: string;
  password: string;
  /** L12 — required when the account has TOTP enrolled. */
  totpCode?: string;
}

export interface LoginResult {
  user: AuthenticatedUser;
  /** L12 — true when the user has the policy bit but no TOTP yet.
   *  The SPA pops a blocking enrollment modal in that case. */
  totpEnrollmentRequired: boolean;
}

/**
 * Login mutation — POST /auth/login-web.
 *
 * The web variant of the auth/login endpoint sets a JWT in an
 * httpOnly cookie (Secure + SameSite=None for cross-domain when the
 * front and back live on different sub-domains in staging/prod).
 * The mobile variant /auth/login-mobile returns the JWT in the
 * response body for native apps that use a Bearer header.
 *
 * Response shape (L12): { user, totpEnrollmentRequired }. We unwrap
 * the user for the rest of the app and pass the flag through so the
 * caller can pop a blocking enrollment modal.
 *
 * When the user has TOTP enrolled and `totpCode` is missing, the
 * server replies 401 with `totp.required` — the caller surfaces an
 * OTP input and re-submits.
 */
export function useLogin() {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);

  return useMutation({
    mutationFn: async (input: LoginInput): Promise<LoginResult> => {
      const { data } = await api.post<{
        user: AuthenticatedUser;
        totpEnrollmentRequired: boolean;
      }>('/auth/login-web', input);
      return {
        user: data.user,
        totpEnrollmentRequired: data.totpEnrollmentRequired ?? false,
      };
    },
    onSuccess: (result) => {
      queryClient.setQueryData(authKeys.me(), result.user);
      setUser(result.user);
    },
  });
}
